import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, 
  Music as MusicIcon,
  Users,
  FileAudio,
  Image as ImageIcon,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  CloudUpload,
  X,
  Clock,
  DollarSign,
  Tag,
  Calendar,
  AlertTriangle,
  Volume2,
  PlayCircle
} from 'lucide-react';
import { musicService, digitalOceanService } from '../services/api';

const AdminMusicUpload = () => {
  const navigate = useNavigate();
  const [genres, setGenres] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    cover: { progress: 0, status: 'idle', fileName: '', error: null },
    previewAudio: { progress: 0, status: 'idle', fileName: '', error: null },
    fullAudio: { progress: 0, status: 'idle', fileName: '', error: null }
  });
  
  const [uploadedFiles, setUploadedFiles] = useState({
    coverUrl: '',
    previewAudioUrl: '',
    fullAudioUrl: '',
    coverPreview: '',
    coverDimensions: { width: 0, height: 0, aspectRatio: 0 }
  });
  
  const [audioMetadata, setAudioMetadata] = useState({
    duration: 0,
    previewDuration: 90,
    bitrate: 320,
    fileSize: 0
  });
  
  const [audioPreview, setAudioPreview] = useState({
    isPlaying: false,
    currentTime: 0,
    duration: 0
  });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    artist_id: '',
    genre: '',
    featuring: [],
    price: '',
    age_rating: 'all',
    release_date: new Date().toISOString().split('T')[0],
    status: 'approved',
    is_coming_soon: false,
    artist_name: '',
    artist_email: '',
    artist_bio: '',
    artist_profile_image_url: '',
  });

  const [artistPhotoUploading, setArtistPhotoUploading] = useState(false);
  const [artistPhotoPreview, setArtistPhotoPreview] = useState('');
  const artistPhotoInputRef = useRef(null);

  // Edit artist modal state
  const [editArtistModal, setEditArtistModal] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null); // { id, stage_name, profile_image }
  const [editArtistData, setEditArtistData] = useState({ stage_name: '', profile_image_url: '' });
  const [editArtistPhotoPreview, setEditArtistPhotoPreview] = useState('');
  const [editArtistPhotoUploading, setEditArtistPhotoUploading] = useState(false);
  const [editArtistSaving, setEditArtistSaving] = useState(false);
  const [editArtistError, setEditArtistError] = useState('');
  const editArtistPhotoRef = useRef(null);

  const audioRef = useRef(null);

  // Define constants for dimensions
  const MIN_WIDTH = 300;
  const MIN_HEIGHT = 300;
  const TARGET_RATIO = 1.0;
  const MAX_SIZE_MB = 5;

  const ageRatings = [
    { value: 'all', label: 'All Ages (0+)' },
    { value: 'teen', label: 'Teen (13-17)' },
    { value: 'adult', label: 'Adult (18+)' },
  ];

  const audioFormats = ['mp3', 'wav', 'ogg', 'm4a'];
  const maxFileSize = 50 * 1024 * 1024;

  // Fetch genres and artists
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [genresResponse, artistsResponse] = await Promise.all([
          musicService.getGenres(),
          musicService.getArtists()
        ]);
        
        console.log('Genres fetched:', genresResponse);
        console.log('Artists fetched:', artistsResponse);
        
        setGenres(genresResponse || []);
        setArtists(artistsResponse || []);
        
      } catch (error) {
        console.error('Error fetching data:', error);
        setGenres([
          { id: 1, name: 'Hip Hop' },
          { id: 2, name: 'Afrobeats' },
          { id: 3, name: 'Highlife' },
          { id: 4, name: 'Gospel' },
          { id: 5, name: 'R&B' },
          { id: 6, name: 'Pop' },
        ]);
        setArtists([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleArtistChange = (e) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      artist_id: value
    });
    
    // If selecting existing artist, clear name/email fields
    if (value && value !== 'new') {
      setFormData(prev => ({
        ...prev,
        artist_name: '',
        artist_email: '',
        artist_profile_image_url: '',
      }));
    }
  };

  const handleFeaturingChange = (artistId) => {
    setFormData(prev => {
      const isSelected = prev.featuring.includes(artistId);
      if (isSelected) {
        return {
          ...prev,
          featuring: prev.featuring.filter(id => id !== artistId)
        };
      } else {
        return {
          ...prev,
          featuring: [...prev.featuring, artistId]
        };
      }
    });
  };

  // Handle cover upload
  const handleCoverUpload = async (file) => {
    if (!file) return;

    try {
      setUploadProgress(prev => ({
        ...prev,
        cover: { progress: 0, status: 'uploading', fileName: file.name, error: null }
      }));

      const previewUrl = URL.createObjectURL(file);
      setUploadedFiles(prev => ({
        ...prev,
        coverPreview: previewUrl
      }));

      const uploadData = await digitalOceanService.generateMusicCoverUploadURL({
        filename: file.name
      });

      if (!uploadData.success) {
        throw new Error(uploadData.error || 'Failed to get upload URL');
      }

      await digitalOceanService.uploadToSpaces(
        uploadData.upload_url,
        uploadData.fields,
        file
      );

      setUploadProgress(prev => ({
        ...prev,
        cover: { ...prev.cover, status: 'success', progress: 100 }
      }));

      setUploadedFiles(prev => ({
        ...prev,
        coverUrl: uploadData.public_url
      }));

    } catch (error) {
      setUploadProgress(prev => ({
        ...prev,
        cover: { 
          ...prev.cover, 
          status: 'error', 
          error: error.message 
        }
      }));

      if (uploadedFiles.coverPreview) {
        URL.revokeObjectURL(uploadedFiles.coverPreview);
      }

      setUploadedFiles(prev => ({
        ...prev,
        coverPreview: ''
      }));
    }
  };

  // Handle audio upload
  const handleAudioUpload = async (file, type) => {
    if (!file) return;

    try {
      // Validate file type
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (!audioFormats.includes(fileExtension)) {
        throw new Error(`Invalid audio format. Supported formats: ${audioFormats.join(', ')}`);
      }

      // Validate file size
      if (file.size > maxFileSize) {
        throw new Error(`Audio file must be less than ${maxFileSize / (1024 * 1024)}MB`);
      }

      setUploadProgress(prev => ({
        ...prev,
        [type]: { progress: 0, status: 'uploading', fileName: file.name, error: null }
      }));

      const uploadData = await digitalOceanService.generateMusicAudioUploadURL({
        filename: file.name,
        type: type === 'previewAudio' ? 'preview' : 'full'
      });

      if (!uploadData.success) {
        throw new Error(uploadData.error || 'Failed to get upload URL');
      }

      await digitalOceanService.uploadToSpaces(
        uploadData.upload_url,
        uploadData.fields,
        file
      );

      setUploadProgress(prev => ({
        ...prev,
        [type]: { ...prev[type], status: 'success', progress: 100 }
      }));

      setUploadedFiles(prev => ({
        ...prev,
        [`${type}Url`]: uploadData.public_url
      }));

      // Get audio duration for preview
      if (type === 'previewAudio') {
        const audio = new Audio();
        audio.onloadedmetadata = () => {
          setAudioMetadata(prev => ({
            ...prev,
            previewDuration: Math.min(Math.round(audio.duration), 90)
          }));
        };
        audio.src = uploadData.public_url;
      }

      if (type === 'fullAudio') {
        setAudioMetadata(prev => ({
          ...prev,
          fileSize: file.size
        }));
      }

    } catch (error) {
      console.error(`${type} upload error:`, error);
      setUploadProgress(prev => ({
        ...prev,
        [type]: { 
          ...prev[type], 
          status: 'error', 
          error: error.message || 'Upload failed' 
        }
      }));
    }
  };

  const handleFileSelect = (type) => (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (type === 'cover') {
      handleCoverUpload(file);
    } else if (type === 'previewAudio' || type === 'fullAudio') {
      handleAudioUpload(file, type);
    }
  };

  const clearCoverImage = () => {
    if (uploadedFiles.coverPreview) {
      URL.revokeObjectURL(uploadedFiles.coverPreview);
    }
    
    setUploadedFiles(prev => ({
      ...prev,
      coverUrl: '',
      coverPreview: '',
      coverDimensions: { width: 0, height: 0, aspectRatio: 0 }
    }));
    
    setUploadProgress(prev => ({
      ...prev,
      cover: { progress: 0, status: 'idle', fileName: '', error: null }
    }));
  };

  const clearAudioFile = (type) => {
    setUploadedFiles(prev => ({
      ...prev,
      [`${type}Url`]: ''
    }));
    
    setUploadProgress(prev => ({
      ...prev,
      [type]: { progress: 0, status: 'idle', fileName: '', error: null }
    }));
    
    if (type === 'previewAudio' && audioRef.current) {
      audioRef.current.pause();
      setAudioPreview({
        isPlaying: false,
        currentTime: 0,
        duration: 0
      });
    }
  };

  // Audio player controls
  const handlePlayPreview = () => {
    if (!uploadedFiles.previewAudioUrl) return;
    
    if (audioRef.current) {
      if (audioPreview.isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setAudioPreview(prev => ({ ...prev, isPlaying: !prev.isPlaying }));
    }
  };

  const handleArtistPhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => setArtistPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);

    setArtistPhotoUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const urlRes = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.silkspace.co/api'}/upload/generate-url/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${token}` },
        body: JSON.stringify({ filename: file.name, filetype: 'artist_profile' }),
      });
      const urlData = await urlRes.json();
      if (!urlData.success) throw new Error(urlData.error || 'Could not get upload URL');

      const { upload_url, fields, public_url } = urlData;

      const formDataUpload = new FormData();
      Object.entries(fields || {}).forEach(([k, v]) => formDataUpload.append(k, v));
      formDataUpload.append('file', file);

      const uploadRes = await fetch(upload_url, { method: 'POST', body: formDataUpload });
      if (uploadRes.status !== 200 && uploadRes.status !== 204) {
        throw new Error(`Upload failed: ${uploadRes.status}`);
      }

      setFormData(prev => ({ ...prev, artist_profile_image_url: public_url }));
      setArtistPhotoPreview(public_url);
    } catch (err) {
      console.error('Artist photo upload error:', err);
      alert(`Photo upload failed: ${err.message}`);
    } finally {
      setArtistPhotoUploading(false);
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      alert('Please enter a title');
      return false;
    }

    if (!formData.description.trim()) {
      alert('Please enter a description');
      return false;
    }

    if (!formData.artist_id) {
      alert('Please select an artist or create a new one');
      return false;
    }

      if (formData.artist_id === 'new' && (!formData.artist_name.trim() || !formData.artist_email.trim())) {
      alert('Please provide name and email for the new artist');
      return false;
    }

    if (!uploadedFiles.coverUrl) {
      alert('Please upload a cover image');
      return false;
    }

    if (!uploadedFiles.fullAudioUrl) {
      alert('Please upload the full audio file');
      return false;
    }

    if (!uploadedFiles.previewAudioUrl) {
      alert('Please upload a preview audio file');
      return false;
    }

    if (!formData.price || parseFloat(formData.price) < 0) {
      alert('Please enter a valid price (0 or higher)');
      return false;
    }

    return true;
  };

  // ── Edit Artist handlers ──────────────────────────────────────────────
  const openEditArtist = (artist) => {
    setEditingArtist(artist);
    setEditArtistData({ stage_name: artist.stage_name || '', profile_image_url: artist.profile_image || '' });
    setEditArtistPhotoPreview(artist.profile_image || '');
    setEditArtistError('');
    setEditArtistModal(true);
  };

  const handleEditArtistPhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!file.type.startsWith('image/')) { alert('Please select an image file.'); return; }
    if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB.'); return; }

    // Local preview immediately
    const reader = new FileReader();
    reader.onload = (ev) => setEditArtistPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);

    setEditArtistPhotoUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const urlRes = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.silkspace.co/api'}/upload/generate-url/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${token}` },
        body: JSON.stringify({ filename: file.name, filetype: 'artist_profile' }),
      });
      const urlData = await urlRes.json();
      if (!urlData.success) throw new Error(urlData.error || 'Could not get upload URL');
      const { upload_url, fields, public_url } = urlData;
      const formDataUpload = new FormData();
      Object.entries(fields || {}).forEach(([k, v]) => formDataUpload.append(k, v));
      formDataUpload.append('file', file);
      const uploadRes = await fetch(upload_url, { method: 'POST', body: formDataUpload });
      if (uploadRes.status !== 200 && uploadRes.status !== 204) throw new Error(`Upload failed: ${uploadRes.status}`);
      setEditArtistData(prev => ({ ...prev, profile_image_url: public_url }));
      setEditArtistPhotoPreview(public_url);
    } catch (err) {
      alert(`Photo upload failed: ${err.message}`);
    } finally {
      setEditArtistPhotoUploading(false);
    }
  };

  const handleSaveEditArtist = async () => {
    if (!editingArtist) return;
    if (!editArtistData.stage_name.trim()) { setEditArtistError('Stage name is required.'); return; }
    setEditArtistSaving(true);
    setEditArtistError('');
    try {
      const { voucherService } = await import('../services/api');
      await voucherService.adminUpdateArtist(editingArtist.id, {
        stage_name: editArtistData.stage_name.trim(),
        profile_image_url: editArtistData.profile_image_url || '',
      });
      // Refresh artist list
      const { musicService } = await import('../services/api');
      const updated = await voucherService.getArtists();
      setArtists(updated || []);
      setEditArtistModal(false);
      setEditingArtist(null);
    } catch (err) {
      setEditArtistError(err.message || 'Failed to save. Please try again.');
    } finally {
      setEditArtistSaving(false);
    }
  };

    const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      const musicData = {
        title: formData.title,
        description: formData.description,
        artist_id: formData.artist_id,
        genre: formData.genre || null,
        price: parseFloat(formData.price) || 0,
        cover_image_url: uploadedFiles.coverUrl,
        preview_audio_url: uploadedFiles.previewAudioUrl,
        full_audio_url: uploadedFiles.fullAudioUrl,
        duration: audioMetadata.duration,
        preview_duration: audioMetadata.previewDuration,
        bitrate: audioMetadata.bitrate,
        file_size: audioMetadata.fileSize,
        age_rating: formData.age_rating,
        release_date: formData.release_date,
        status: formData.status,
        is_coming_soon: formData.is_coming_soon,
        // Ensure featuring IDs are numbers (backend expects integers)
        featuring: formData.featuring.map(id => {
          const parsed = parseInt(id, 10);
          return isNaN(parsed) ? id : parsed;
        }).filter(id => id !== null && id !== undefined),
        // New artist data
        artist_name: formData.artist_name,
        artist_email: formData.artist_email,
        artist_bio: formData.artist_bio || '',
        artist_profile_image_url: formData.artist_profile_image_url || ''
      };

      console.log('Submitting music data:', musicData);
      const response = await musicService.adminUploadMusic(musicData);
      
      alert(`✅ Music uploaded successfully!`);
      navigate(`/admin/music`);
      
    } catch (error) {
      console.error('❌ Error uploading music:', error);
      alert(`Upload failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const UploadProgressBar = ({ type, progress, fileName, status, error }) => {
    return (
      <div className="mt-3 bg-gray-50 rounded-lg p-3 border border-gray-200">
        <div className="flex items-center justify-between mb-1">
          <div className="text-sm font-medium text-gray-700 truncate">
            {fileName}
          </div>
          <div className="text-sm text-gray-500">
            {status === 'uploading' ? `${progress}%` : status}
          </div>
        </div>
        
        {status === 'uploading' && (
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
        
        {status === 'success' && (
          <div className="flex items-center text-green-600 text-sm">
            <CheckCircle className="h-4 w-4 mr-1" />
            Upload complete
          </div>
        )}
        
        {status === 'error' && (
          <div className="flex items-center text-red-600 text-sm">
            <AlertCircle className="h-4 w-4 mr-1" />
            {error}
          </div>
        )}
      </div>
    );
  };

  // Format seconds to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-full bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/music')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Upload Music</h1>
                <p className="text-gray-600 font-medium">
                  Add new music to the platform as an administrator
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Specifications Card */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-300 rounded-xl p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-800 font-bold mb-2">
                ⚠️ MUSIC UPLOAD REQUIREMENTS
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center text-sm text-blue-700 font-medium">
                  <ImageIcon className="h-4 w-4 mr-2" />
                  <span><strong>Cover Image:</strong> Square (min 300×300px, max 5MB)</span>
                </div>
                <div className="flex items-center text-sm text-blue-700 font-medium">
                  <Volume2 className="h-4 w-4 mr-2" />
                  <span><strong>Audio Formats:</strong> MP3, WAV, OGG, M4A</span>
                </div>
                <div className="flex items-center text-sm text-blue-700">
                  <Clock className="h-4 w-4 mr-2" />
                  <span><strong>Preview Audio:</strong> Max 90 seconds</span>
                </div>
                <div className="flex items-center text-sm text-blue-700">
                  <FileAudio className="h-4 w-4 mr-2" />
                  <span><strong>Full Audio:</strong> Max 50MB</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Form */}
        <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
          <form onSubmit={handleSubmit} className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Music Details */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <MusicIcon className="h-5 w-5 mr-2 text-[#1d1d1d]" />
                    Music Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title *
                      </label>
                      <input
                        type="text"
                        name="title"
                        required
                        value={formData.title}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        placeholder="Enter music title"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description *
                      </label>
                      <textarea
                        name="description"
                        required
                        value={formData.description}
                        onChange={handleChange}
                        rows="3"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors resize-none"
                        placeholder="Describe the music..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Genre
                        </label>
                        <select
                          name="genre"
                          value={formData.genre}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        >
                          <option value="">Select Genre</option>
                          {genres.map((genre) => (
                            <option key={genre.id} value={genre.id}>
                              {genre.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Age Rating *
                        </label>
                        <select
                          name="age_rating"
                          required
                          value={formData.age_rating}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        >
                          {ageRatings.map((rating) => (
                            <option key={rating.value} value={rating.value}>
                              {rating.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Price (GHS) *
                        </label>
                        <input
                          type="number"
                          name="price"
                          required
                          min="0"
                          step="0.01"
                          value={formData.price}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Release Date
                        </label>
                        <input
                          type="date"
                          name="release_date"
                          value={formData.release_date}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        />
                      </div>
                    </div>

                    {/* Featuring Artists */}
                    {artists.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Featuring Artists (Optional)
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          {artists.map((artist) => (
                            <label
                              key={artist.id}
                              className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                                formData.featuring.includes(artist.id)
                                  ? 'bg-blue-50 border-blue-300'
                                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={formData.featuring.includes(artist.id)}
                                onChange={() => handleFeaturingChange(artist.id)}
                                className="mr-3 h-4 w-4 text-blue-600"
                              />
                              <Users className="h-4 w-4 mr-2 text-gray-500" />
                              <span className="text-sm">{artist.stage_name || artist.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Artist & Files */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-[#1d1d1d]" />
                    Artist Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Artist *
                      </label>
                      <select
                        name="artist_id"
                        value={formData.artist_id}
                        onChange={handleArtistChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                      >
                        <option value="">Select Existing Artist</option>
                        {artists.map((artist) => (
                          <option key={artist.id} value={artist.id}>
                            {artist.stage_name || artist.name}
                          </option>
                        ))}
                        <option value="new">➕ Add New Artist</option>
                      </select>
                      {/* Edit selected artist */}
                      {formData.artist_id && formData.artist_id !== 'new' && (() => {
                        const sel = artists.find(a => String(a.id) === String(formData.artist_id));
                        return sel ? (
                          <button
                            type="button"
                            onClick={() => openEditArtist(sel)}
                            className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#1d1d1d] font-semibold border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                            Edit Artist Profile
                          </button>
                        ) : null;
                      })()}
                    </div>

                    {formData.artist_id === 'new' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Artist Name *
                          </label>
                          <input
                            type="text"
                            name="artist_name"
                            value={formData.artist_name}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                            placeholder="Enter artist's name"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Artist Email *
                          </label>
                          <input
                            type="email"
                            name="artist_email"
                            value={formData.artist_email}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                            placeholder="Enter artist's email"
                          />
                        </div>

                        <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Artist Bio
      </label>
      <textarea
        name="artist_bio"
        value={formData.artist_bio}
        onChange={handleChange}
        rows="3"
        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors resize-none"
        placeholder="Tell us about the artist..."
      />
    </div>

                        {/* ── Artist Profile Photo ── */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Artist Profile Photo <span className="text-gray-400 font-normal">(optional)</span>
                          </label>
                          <input
                            ref={artistPhotoInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleArtistPhotoSelect}
                          />
                          <div className="flex items-center gap-4">
                            {/* Preview circle */}
                            <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-200 bg-gray-100 flex-shrink-0 flex items-center justify-center">
                              {artistPhotoPreview ? (
                                <img src={artistPhotoPreview} alt="Artist preview" className="w-full h-full object-cover" />
                              ) : (
                                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              )}
                            </div>
                            <div className="flex-1">
                              <button
                                type="button"
                                onClick={() => artistPhotoInputRef.current?.click()}
                                disabled={artistPhotoUploading}
                                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {artistPhotoUploading ? (
                                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600" /><span>Uploading…</span></>
                                ) : artistPhotoPreview ? (
                                  <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg><span>Change Photo</span></>
                                ) : (
                                  <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg><span>Upload Photo</span></>
                                )}
                              </button>
                              <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP — max 5 MB. This photo will show on the artist's public page.</p>
                              {formData.artist_profile_image_url && (
                                <button
                                  type="button"
                                  onClick={() => { setArtistPhotoPreview(''); setFormData(prev => ({ ...prev, artist_profile_image_url: '' })); }}
                                  className="text-xs text-red-500 hover:text-red-700 mt-1"
                                >
                                  Remove photo
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <ImageIcon className="h-5 w-5 mr-2 text-[#1d1d1d]" />
                    Music Files (Direct Cloud Upload)
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Cover Image Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cover Image * <span className="text-blue-600 text-xs">(Square, min 300×300px)</span>
                      </label>
                      
                      {/* Image Preview */}
                      {(uploadedFiles.coverPreview || uploadedFiles.coverUrl) && (
                        <div className="mb-4 relative">
                          <div className="relative w-40 h-40 mx-auto">
                            <img
                              src={uploadedFiles.coverPreview || uploadedFiles.coverUrl}
                              alt="Cover preview"
                              className="w-full h-full object-cover rounded-lg shadow-md border border-gray-300"
                              style={{ aspectRatio: '1/1' }}
                            />
                            
                            {/* Clear Button */}
                            <button
                              type="button"
                              onClick={clearCoverImage}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                      
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                        <input
                          type="file"
                          id="cover-upload"
                          accept="image/*"
                          onChange={handleFileSelect('cover')}
                          className="hidden"
                        />
                        <label
                          htmlFor="cover-upload"
                          className="cursor-pointer block"
                        >
                          {uploadedFiles.coverPreview ? (
                            <div className="space-y-2">
                              <div className="flex items-center justify-center">
                                <Upload className="h-8 w-8 text-gray-400" />
                              </div>
                              <p className="text-sm text-gray-600">
                                Replace cover image
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <ImageIcon className="h-10 w-10 text-gray-400 mx-auto" />
                              <div>
                                <p className="text-sm font-medium text-gray-700">Upload Cover Image</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Click or drag to upload
                                </p>
                              </div>
                            </div>
                          )}
                        </label>
                      </div>
                      
                      {uploadProgress.cover.status !== 'idle' && (
                        <UploadProgressBar
                          type="cover"
                          progress={uploadProgress.cover.progress}
                          fileName={uploadProgress.cover.fileName}
                          status={uploadProgress.cover.status}
                          error={uploadProgress.cover.error}
                        />
                      )}
                    </div>

                    {/* Full Audio Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Audio File *
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                        <input
                          type="file"
                          id="full-audio-upload"
                          accept=".mp3,.wav,.ogg,.m4a"
                          onChange={handleFileSelect('fullAudio')}
                          className="hidden"
                        />
                        <label
                          htmlFor="full-audio-upload"
                          className="cursor-pointer block"
                        >
                          {uploadedFiles.fullAudioUrl ? (
                            <div className="space-y-2">
                              <FileAudio className="h-8 w-8 text-green-500 mx-auto" />
                              <p className="text-sm text-green-600 font-medium">
                                ✓ Full audio uploaded
                              </p>
                              <button
                                type="button"
                                onClick={() => clearAudioFile('fullAudio')}
                                className="text-xs text-red-600 hover:text-red-700"
                              >
                                Remove
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <FileAudio className="h-8 w-8 text-gray-400 mx-auto" />
                              <p className="text-sm text-gray-600">
                                Click to upload full audio
                              </p>
                              <p className="text-xs text-gray-500">
                                MP3, WAV, OGG, M4A (max 50MB)
                              </p>
                            </div>
                          )}
                        </label>
                      </div>
                      
                      {uploadProgress.fullAudio.status !== 'idle' && (
                        <UploadProgressBar
                          type="fullAudio"
                          progress={uploadProgress.fullAudio.progress}
                          fileName={uploadProgress.fullAudio.fileName}
                          status={uploadProgress.fullAudio.status}
                          error={uploadProgress.fullAudio.error}
                        />
                      )}
                    </div>

                    {/* Preview Audio Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preview Audio * <span className="text-blue-600 text-xs">(Max 90 seconds)</span>
                      </label>
                      
                      {/* Preview Player */}
                      {uploadedFiles.previewAudioUrl && (
                        <div className="mb-4 bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={handlePlayPreview}
                                className="w-10 h-10 rounded-full bg-[#1d1d1d] text-white flex items-center justify-center hover:bg-gray-800 transition-colors"
                              >
                                {audioPreview.isPlaying ? (
                                  <div className="h-4 w-4 bg-white"></div>
                                ) : (
                                  <PlayCircle className="h-5 w-5" />
                                )}
                              </button>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  Preview Audio
                                </p>
                                <p className="text-xs text-gray-500">
                                  Click to play preview
                                </p>
                              </div>
                            </div>
                          </div>
                          <audio
                            ref={audioRef}
                            src={uploadedFiles.previewAudioUrl}
                            onTimeUpdate={(e) => setAudioPreview(prev => ({ 
                              ...prev, 
                              currentTime: e.target.currentTime,
                              duration: e.target.duration 
                            }))}
                            onEnded={() => setAudioPreview(prev => ({ ...prev, isPlaying: false }))}
                            preload="metadata"
                          />
                        </div>
                      )}
                      
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                        <input
                          type="file"
                          id="preview-audio-upload"
                          accept=".mp3,.wav,.ogg,.m4a"
                          onChange={handleFileSelect('previewAudio')}
                          className="hidden"
                        />
                        <label
                          htmlFor="preview-audio-upload"
                          className="cursor-pointer block"
                        >
                          {uploadedFiles.previewAudioUrl ? (
                            <div className="space-y-2">
                              <FileAudio className="h-8 w-8 text-blue-500 mx-auto" />
                              <p className="text-sm text-blue-600 font-medium">
                                ✓ Preview audio uploaded
                              </p>
                              <button
                                type="button"
                                onClick={() => clearAudioFile('previewAudio')}
                                className="text-xs text-red-600 hover:text-red-700"
                              >
                                Remove
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <FileAudio className="h-8 w-8 text-gray-400 mx-auto" />
                              <p className="text-sm text-gray-600">
                                Click to upload preview audio
                              </p>
                              <p className="text-xs text-gray-500">
                                Max 90 seconds, 10MB
                              </p>
                            </div>
                          )}
                        </label>
                      </div>
                      
                      {uploadProgress.previewAudio.status !== 'idle' && (
                        <UploadProgressBar
                          type="previewAudio"
                          progress={uploadProgress.previewAudio.progress}
                          fileName={uploadProgress.previewAudio.fileName}
                          status={uploadProgress.previewAudio.status}
                          error={uploadProgress.previewAudio.error}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Status Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Initial Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                  >
                    <option value="approved">Approved (Publish Immediately)</option>
                    <option value="pending">Pending Review</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>

                {/* Coming Soon Toggle */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="is_coming_soon"
                    name="is_coming_soon"
                    checked={formData.is_coming_soon}
                    onChange={handleChange}
                    className="h-4 w-4 text-[#1d1d1d]"
                  />
                  <label htmlFor="is_coming_soon" className="text-sm font-medium text-gray-700">
                    Mark as Coming Soon
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || !uploadedFiles.coverUrl || !uploadedFiles.fullAudioUrl || !uploadedFiles.previewAudioUrl}
                  className="w-full bg-gradient-to-r from-[#ff9702] to-[#ffaa33] text-white py-4 rounded-lg font-semibold hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CloudUpload className="h-4 w-4 mr-2" />
                      Submit Music
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* ── Edit Artist Modal ─────────────────────────────────────── */}
      {editArtistModal && editingArtist && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Edit Artist</h3>
              <button onClick={() => { setEditArtistModal(false); setEditingArtist(null); }}
                className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 transition-colors">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-6 py-5 space-y-5">
              <div className="flex items-center gap-5">
                <div className="relative flex-shrink-0">
                  <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-200 bg-gray-100 flex items-center justify-center">
                    {editArtistPhotoPreview ? (
                      <img src={editArtistPhotoPreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-gray-400 text-2xl font-black">
                        {(editingArtist.stage_name || '?')[0].toUpperCase()}
                      </span>
                    )}
                  </div>
                  {editArtistPhotoUploading && (
                    <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <div>
                  <input ref={editArtistPhotoRef} type="file" accept="image/*" className="hidden" onChange={handleEditArtistPhotoSelect} />
                  <button type="button" onClick={() => editArtistPhotoRef.current?.click()}
                    disabled={editArtistPhotoUploading}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    {editArtistPhotoUploading ? 'Uploading…' : editArtistPhotoPreview ? 'Change Photo' : 'Upload Photo'}
                  </button>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP — max 5 MB</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Stage Name *</label>
                <input type="text" value={editArtistData.stage_name}
                  onChange={e => setEditArtistData(prev => ({ ...prev, stage_name: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-[#ff9702] transition-colors"
                  placeholder="Artist stage name" />
              </div>
              {editArtistError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">{editArtistError}</div>
              )}
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button onClick={() => { setEditArtistModal(false); setEditingArtist(null); }}
                className="flex-1 py-3 rounded-xl font-semibold text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors">
                Cancel
              </button>
              <button onClick={handleSaveEditArtist} disabled={editArtistSaving || editArtistPhotoUploading}
                className="flex-1 py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-[#ff9702] to-[#ffaa33] text-white flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                {editArtistSaving
                  ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Saving…</span></>
                  : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default AdminMusicUpload;